import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";
import { LeadTimeLines } from "@/components/shop/LeadTimeNote";
import { getJson } from "@/lib/publicApi";
import { usePageMeta } from "@/lib/seo";
import { SITE } from "@/config/site";
import { PAGE_VICHY_FIT, pageVichy } from "@/lib/patternBg";

// Cores do círculo do ícone: verde (feito), bordeaux (atenção) e neutro (a aguardar ou cancelado).
const TONES = {
  ok: { icon: "text-[#3F5639]", badge: "bg-[#3F5639]/10 ring-[#3F5639]/5" },
  alert: { icon: "text-bordeaux", badge: "bg-rose-soft ring-rose-soft/50" },
  calm: { icon: "text-taupe", badge: "bg-linen ring-linen/60" },
};

const Box = ({ icon: Icon, tone = "calm", eyebrow, title, children, testId, spin = false }) => (
  <div className={`${PAGE_VICHY_FIT} pb-[calc(4rem+30px)]`} style={pageVichy()} data-testid={testId}>
   <div className="bb-container max-w-2xl py-14 text-center sm:py-20">
    <span className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ring-8 ${TONES[tone].badge}`}>
      <Icon className={`h-9 w-9 ${TONES[tone].icon} ${spin ? "animate-pulse" : ""}`} />
    </span>
    {eyebrow && <p className="bb-eyebrow mt-6">{eyebrow}</p>}
    <h1 className={`${eyebrow ? "mt-2" : "mt-6"} font-serif text-4xl leading-tight sm:text-5xl`}>{title}</h1>
    <div className="mx-auto mt-5 flex max-w-md flex-col items-center gap-6 text-base text-taupe">{children}</div>
   </div>
  </div>
);

export function OrderConfirmationPage() {
  usePageMeta("Encomenda");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") ?? "";
  const { clear } = useCart();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) return undefined;
    let alive = true;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await getJson(`/payments/status/${encodeURIComponent(sessionId)}`);
        if (!alive) return;
        setData(r);
        if (r.payment_status === "pending" && tries++ < 12) timer = setTimeout(poll, 2500);
      } catch (e) {
        if (alive) setError(e.message);
      }
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [sessionId]);

  useEffect(() => {
    if (data?.payment_status === "paid") clear();
  }, [data?.payment_status, clear]);

  if (!sessionId || error) return <Box icon={XCircle} tone="alert" eyebrow="Algo não correu bem" title="Não encontrei este pagamento" testId="order-not-found">{error && <p>{error}</p>}<Link to="/carrinho" className="bb-btn-primary">Voltar ao carrinho</Link></Box>;
  if (!data) return <Box icon={Clock} spin eyebrow="Só um momento" title="A confirmar o pagamento..." testId="order-checking"><p>Isto demora só alguns segundos.</p></Box>;
  if (data.payment_status === "paid") {
    return (
      <div className={`${PAGE_VICHY_FIT} pb-[calc(4rem+30px)]`} style={pageVichy()} data-testid="order-paid">
      <div className="bb-container max-w-2xl py-14 text-center sm:py-20">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#3F5639]/10 ring-8 ring-[#3F5639]/5">
          <CheckCircle2 className="h-9 w-9 text-[#3F5639]" />
        </span>
        <p className="bb-eyebrow mt-6">Pagamento confirmado</p>
        <h1 className="mt-2 font-serif text-4xl leading-tight sm:text-5xl">Obrigada pela tua encomenda!</h1>
        <p className="mx-auto mt-4 max-w-md text-base text-taupe">Enviei a confirmação para o teu email. Vou começar a preparar tudo com muito carinho.</p>

        <div className="mt-8 rounded-3xl bg-linen p-5 text-left sm:p-8">
          <div className="flex items-center justify-between gap-3 border-b border-ink/10 pb-4">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-bordeaux">Encomenda</span>
            <b className="rounded-full bg-white/70 px-3 py-1 text-sm text-ink" data-testid="order-number">{data.order_number}</b>
          </div>
          <ul className="divide-y divide-ink/10 text-sm text-ink/85">
            {(data.items ?? []).map((i) => (
              <li key={`${i.name}-${i.pattern}-${i.personalization}`} className="flex gap-3 py-3">
                <span className="mt-0.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-rose-soft px-1.5 text-xs font-semibold text-bordeaux">{i.qty}</span>
                <span>
                  <span className="block font-medium text-ink">{i.name}</span>
                  <span className="block text-taupe">{i.reversible && i.slots ? i.slots : i.pattern}{i.personalization ? ` · ${i.personalization}` : ""}</span>
                  {i.note && <span className="block text-xs italic text-taupe">Comentário: {i.note}</span>}
                  {i.phrase_text && <span className="block text-ink" data-testid="order-phrase-text">Texto do saquinho: <span className="font-medium">{i.phrase_text}</span></span>}
                </span>
              </li>
            ))}
          </ul>
          <dl className="space-y-1.5 border-t border-ink/10 pt-4 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-taupe">Envio</dt><dd className="text-right text-ink/85">{data.shipping}</dd></div>
            <div className="flex justify-between gap-3 text-base"><dt className="font-medium text-ink">Total</dt><dd className="font-semibold text-ink">{formatPrice(data.total)}</dd></div>
          </dl>
        </div>

        <div className="mt-4 rounded-2xl bg-ivory px-5 py-5 text-left ring-1 ring-ink/[0.06]" data-testid="order-next-steps">
          <p className="font-display text-[1.0625rem] text-ink">E agora, o que acontece?</p>
          <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-ink/85">
            <li>Cada peça é feita à mão, só depois de fazeres a encomenda. Por isso preparo os pedidos <b className="text-ink">por ordem de chegada</b>.</li>
            <li>O prazo habitual é de <b className="text-ink">2 a 4 semanas</b>, conforme os pedidos que tenho na fila. Se precisares dela para uma data em particular, diz-me e vejo o que posso fazer.</li>
            <li>Vais receber um email quando a encomenda entrar em produção e outro quando for enviada.</li>
            <li>Tens alguma dúvida ou queres saber em que fase está o teu pedido? Fala comigo no Instagram: <a href={SITE.instagram.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-bordeaux underline underline-offset-2" data-testid="order-instagram">{SITE.instagram.handle}</a>. Diz-me o número da encomenda ({data.order_number}) para eu a encontrar logo.</li>
          </ul>
        </div>

        {(data.lead_time || data.pattern_extra) && (
          <div className="mt-4 rounded-2xl bg-rose-soft/70 px-5 py-4 text-left" data-testid="order-lead-time">
            <p className="font-display text-[1.0625rem] text-ink">Feito com carinho, só para ti 🤍</p>
            <LeadTimeLines lt={data.lead_time} extra={data.pattern_extra} className="mt-2" />
          </div>
        )}
        <Link to="/loja" className="bb-btn-ghost mt-8">Continuar na loja</Link>
      </div>
      </div>
    );
  }
  if (data.payment_status === "pending") {
    return (
      <Box icon={Clock} tone="alert" eyebrow="A aguardar" title="Pagamento pendente" testId="order-pending">
        <div className="w-full rounded-3xl bg-linen p-5 text-left text-sm sm:p-6">
          <p className="flex items-center justify-between gap-3 border-b border-ink/10 pb-3">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-bordeaux">Encomenda</span>
            <b className="rounded-full bg-white/70 px-3 py-1 text-ink">{data.order_number}</b>
          </p>
          <p className="pt-3 text-ink/85">Fica registada assim que o pagamento for confirmado. Se escolheste um método com referência (por exemplo, Multibanco), conclui o pagamento e receberás um email.</p>
        </div>
        <Link to="/loja" className="bb-btn-ghost">Continuar na loja</Link>
      </Box>
    );
  }
  return (
    <Box icon={XCircle} tone="alert" eyebrow="Sem cobrança" title="O pagamento não foi concluído" testId="order-failed">
      <div className="w-full rounded-3xl bg-linen p-5 text-left text-sm sm:p-6">
        <p className="flex items-center justify-between gap-3 border-b border-ink/10 pb-3">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-bordeaux">Encomenda</span>
          <b className="rounded-full bg-white/70 px-3 py-1 text-ink">{data.order_number}</b>
        </p>
        <p className="pt-3 text-ink/85">Não foi paga ({data.payment_status === "expired" ? "a sessão expirou" : "o pagamento falhou"}). <b className="text-ink">Não foi cobrado nada.</b> Podes tentar outra vez quando quiseres.</p>
      </div>
      <Link to="/carrinho" className="bb-btn-primary">Voltar ao carrinho</Link>
    </Box>
  );
}

export function OrderCancelledPage() {
  usePageMeta("Pagamento cancelado");
  return (
    <Box icon={XCircle} eyebrow="Sem cobrança" title="Pagamento cancelado" testId="order-cancelled">
      <p>Não foi cobrado nada e o teu carrinho continua guardado. Quando quiseres, é só voltar e concluir a encomenda.</p>
      <div className="flex flex-wrap justify-center gap-3">
        <Link to="/carrinho" className="bb-btn-primary">Voltar ao carrinho</Link>
        <Link to="/loja" className="bb-btn-ghost">Continuar na loja</Link>
      </div>
    </Box>
  );
}
